/**
 * The word around the cursor that a picked completion replaces, and what was
 * typed of it before the cursor.
 */
export interface CompletionWord {
  /**
   * Where the word starts, as an offset into the line.
   */
  from: number
  /**
   * Where the word ends, after the cursor, as an offset into the line.
   */
  to: number
  /**
   * The part of the word before the cursor.
   */
  typed: string
}

const wordChar = /\w/

/**
 * Finds the identifier around the cursor. A `.` ends it, so after `x.` the
 * symbols in scope are listed again.
 *
 * @param line The text of the cursor's line.
 * @param cursor The cursor's offset into the line.
 */
export function findCompletionWord(
  line: string,
  cursor: number,
): CompletionWord {
  let from = cursor
  let to = cursor
  while (from > 0 && wordChar.test(line.charAt(from - 1))) {
    from--
  }
  while (to < line.length && wordChar.test(line.charAt(to))) {
    to++
  }
  return { from, to, typed: line.slice(from, cursor) }
}

/**
 * The names that contain what was typed, ignoring case.
 *
 * @param names The names in scope.
 * @param typed What was typed of the word.
 */
export function filterCompletionNames<T extends { name: string }>(
  names: readonly T[],
  typed: string,
): T[] {
  const search = typed.toLowerCase()
  return search
    ? names.filter((x) => x.name.toLowerCase().includes(search))
    : [...names]
}
